import React, { useContext, useEffect, useState } from 'react';
import styles from './ServerMonitoring.module.css';
import GuiContext from 'config/context/guiContext';
import { RefreshIcon } from 'assets/icons';
import ServerLatencyGraph from './ServerLatencyGraph';
import { useGetServerStatus, useSetServerNode } from 'api/monitoring/useMonitoring';
import ThemeContext from 'config/context/themeContext';

const ServerMonitoring = () => {
    const {
        selected: { selectedServer }
    } = useContext(GuiContext);
    const { dark } = useContext(ThemeContext);
    const { response, loading, error, getServerStatus, lastChecked } = useGetServerStatus();
    const { setServerNode } = useSetServerNode();

    const [serverStatus, setServerStatus] = useState(false);
    const [latencyStatus, setLatencyStatus] = useState(0);
    const [serverHealth, setServerHealth] = useState(0);
    const [avgPing, setAvgPing] = useState(undefined);

    // Obe funkcije su `useCallback` nad serverom i adresom, pa im se identitet
    // menja tačno kad se promeni server — isto što je ranije stajalo u
    // `[selectedServer]`, samo iz jednog izvora.
    useEffect(() => {
        setServerNode();
    }, [setServerNode]);

    useEffect(() => {
        const fetchStatus = () => getServerStatus();

        fetchStatus();
        // Interval se ruši i postavlja iznova samo kad se server promeni, a ne
        // pri svakom iscrtavanju — o tome se stara `useCallback` u servisu.
        const intervalId = setInterval(fetchStatus, 30000);

        return () => clearInterval(intervalId);
    }, [getServerStatus]);

    useEffect(() => {
        if (!loading && !error) {
            if (response) {
                const pings = response?.monitorings || [];
                const lastPing = pings.at(-1);
                setAvgPing(pings.length ? Math.round(pings.reduce((acc, val) => acc + val, 0) / pings.length) : 0);

                if (response?.status && lastPing < 20000) {
                    setServerStatus(true);
                    switch (true) {
                        case lastPing < 500:
                            setLatencyStatus(1); // Normal
                            break;
                        case lastPing < 1000:
                            setLatencyStatus(2); // Slow
                            break;
                        default:
                            setLatencyStatus(3); // Bad
                    }
                } else {
                    setServerStatus(false);
                    setLatencyStatus(3);
                }

                if (pings.length > 1) {
                    switch (true) {
                        case avgPing < 500:
                            setServerHealth(1); // Stable
                            break;
                        case avgPing < 1000:
                            setServerHealth(2); // Degraded
                            break;
                        default:
                            setServerHealth(3); // Unstable
                    }
                }

            } else {
                setServerStatus(false);
                setLatencyStatus(0);
                setServerHealth(0);
            }
        } else {
            setServerStatus(false);
            setLatencyStatus(0);
            setServerHealth(0);
        }
    }, [response, loading, error, avgPing]);


    const getLatencyStatusDetails = (status) => {
        switch (status) {
            case 1: return { color: '#1F7D53', text: 'normal' };
            case 2: return { color: '#E78B48', text: 'slow' };
            case 3: return { color: '#BE3144', text: 'bad' };
            default: return { color: '#DBDBDB', text: 'calculating...' };
        }
    };

    const getServerHealthDetails = (health) => {
        switch (health) {
            case 1: return { color: '#1F7D53', text: 'stable' };
            case 2: return { color: '#E78B48', text: 'degraded' };
            case 3: return { color: '#BE3144', text: 'unstable' };
            default: return { color: '#DBDBDB', text: 'calculating...' };
        }
    };

    const latencyStatusDetails = getLatencyStatusDetails(latencyStatus);
    const healthInfo = getServerHealthDetails(serverHealth);
    const lastPing = response?.monitorings?.at(-1) ?? 'N/A';

    return (
        <div className={`${styles.server_monitoring} ${dark ? styles.dark : ""} `}>
            <div className={styles.server_monitoring__header}>
                <h3>Server monitoring</h3>
                <div className={styles.server_status}>
                    <div className={styles.server_status_indicator}>
                        <div style={{ backgroundColor: serverStatus ? "#1F7D53" : "#DBDBDB" }} className={styles.indicator} />
                        <span>
                            {serverStatus ? "ONLINE" : loading ? "calculating..." : "Offline"}
                        </span>
                    </div>
                    <div className={styles.last_checked}>
                        <span>Last checked: {lastChecked}</span>
                        <button onClick={() => getServerStatus()} className={loading ? styles.refresh_loading : ''}><RefreshIcon /></button>
                    </div>
                </div>
            </div>
            <div className={styles.server_monitoring__statistics}>
                <div className={styles.active_server}>
                    <p>Server URL: <span>{selectedServer}</span></p>
                </div>
                <div className={styles.server_diagnostics}>
                    <div className={styles.server_latency_status}>
                        <div style={{ backgroundColor: latencyStatusDetails.color }} className={styles.indicator} /> <p><span>{latencyStatusDetails.text}</span></p>
                    </div>
                    <div className={styles.server_ping}>
                        <p>ping: <span>{lastPing !== 'N/A' ? `${lastPing}ms` : 'N/A'}</span></p>
                    </div>
                    <div className={styles.server_ping}>
                        <p title="Based on average ping from all checks">health: <span style={{ color: healthInfo.color }}>{healthInfo.text}</span></p>
                    </div>
                    {Array.isArray(response?.monitorings) && response?.monitorings.length > 1 && (
                        <ServerLatencyGraph pingData={response?.monitorings} statusDetails={healthInfo} avgPing={avgPing} />
                    )}
                </div>
            </div>
        </div>
    );
}

export default ServerMonitoring;